import { type ReactElement, type ReactNode, useMemo } from 'react'
import { PrefetchedUserInfo } from 'app/components/entityInfo'
import { getName, type TinyUser, type UserId } from '@terros/common'
import { useTinyUser } from '@front/model'
import { TAlert } from '../alert'
import { LoadingEntityListItem } from './LoadingEntityListItem'
import { TListItem, type TListItemProps } from './ListItem'

export type EntityListItemProps = Omit<TListItemProps, 'label' | 'details'> & {
  avatarSize?: 'sm' | 'md'
  avatarPosition?: 'left' | 'right'
  label?: string | UserValueFn
  details?: ReactNode | UserValueFn
}

type UserValueFn = (props: { user: TinyUser; userName: string }) => ReactNode
type Props = EntityListItemProps & {
  userId: UserId
}

export const UserListItem = ({
  userId,
  label,
  details,
  avatarSize = 'md',
  avatarPosition = 'left',
  ...props
}: Props): ReactElement => {
  const { data, isLoading } = useTinyUser(userId)

  if (isLoading) return <LoadingEntityListItem avatarSize={avatarSize} />

  if (!data) return <TAlert variant='error'>User not found</TAlert>

  return (
    <PrefetchedUserListItem
      user={data}
      label={label}
      details={details}
      avatarSize={avatarSize}
      avatarPosition={avatarPosition}
      {...props}
    />
  )
}

type PrefetchedUserListItemProps = EntityListItemProps & {
  user: TinyUser
}
export const PrefetchedUserListItem = ({
  user,
  label,
  details,
  avatarSize = 'md',
  avatarPosition = 'left',
  ...props
}: PrefetchedUserListItemProps): ReactElement => {
  const resolveValue = (value: ReactNode | UserValueFn | undefined): ReactNode => {
    if (!value) return
    if (typeof value === 'function') {
      return value({ user, userName: getName(user) })
    }
    return value
  }

  const UserAvatar = useMemo(() => {
    return <PrefetchedUserInfo linked={false} avatarOnly size={avatarSize} user={user} />
  }, [user, avatarSize])

  return (
    <TListItem
      leftSection={avatarPosition === 'left' ? UserAvatar : undefined}
      rightSection={avatarPosition === 'right' ? UserAvatar : undefined}
      label={resolveValue(label)}
      details={resolveValue(details)}
      {...props}
    />
  )
}
